/**
 * Re-fetch a conversation whenever my own participant row isn't in it.
 *
 * `unreadCountFor` (store/unread-count.ts) already shows no badge for a
 * conversation like this instead of mis-counting the whole loaded history,
 * but hiding the number isn't the actual fix: the row came back without me
 * in it because the client's mapping of `presenceId` to that conversation is
 * stale or the participants were hydrated before the mapping settled (seen
 * after a storage clear + reload). Getting the conversation again from the
 * server is what brings the real participant row, and with it the real
 * `lastReadAt`, back.
 *
 * Mounted once, app-wide, next to `useConversationList` (both live in
 * `DashboardLayout`) so it watches every conversation the list seeds, not
 * just whichever one is open in the panel.
 */

import { useEffect, useRef } from 'react'
import { useAuthStore } from '@/features/auth/store/auth.store'
import { messengerApi } from '../services/messenger.api'
import { useMessengerStore } from '../store/messenger.store'

/** Don't hammer the same conversation: one attempt per minute is plenty for
 *  something that should only ever need to happen once per bad mapping. */
const RETRY_MS = 60_000
/** A cold start can have many unmapped rows at once; cap how many of them
 *  are in flight together rather than firing one request per conversation. */
const MAX_CONCURRENT = 3
/** A row the server itself returns without me will not fix itself by being
 *  asked again every minute within the same connection period; stop after a
 *  few tries (its badge stays at 0 until reconnecting resets the count). */
const MAX_ATTEMPTS = 3

export function useRemapUnmappedConversations(): void {
  const presenceId = useAuthStore((s) => s.presenceId)
  const upsertWireConversation = useMessengerStore((s) => s.upsertWireConversation)

  const lastAttemptRef = useRef<Map<string, number>>(new Map())
  const attemptsRef = useRef<Map<string, number>>(new Map())
  const inFlightRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    // No presenceId yet means no cursor to compare against either: nothing
    // to remap until it resolves (useContactRegistration owns that).
    if (!presenceId) return

    const scan = (): void => {
      const { wireConversations, wireConversationOrder } = useMessengerStore.getState()
      const now = Date.now()

      for (const id of wireConversationOrder) {
        if (inFlightRef.current.size >= MAX_CONCURRENT) break
        if (inFlightRef.current.has(id)) continue

        const conv = wireConversations.get(id)
        // Participants never hydrated for this row at all: the list endpoint
        // hydrates them for every conversation it returns, so this is "not
        // loaded yet", not "unmapped"; nothing to compare against either way.
        if (!conv?.participants) continue
        const mapped = conv.participants.some((p) => p.userId === presenceId)
        if (mapped) continue

        const attempts = attemptsRef.current.get(id) ?? 0
        if (attempts >= MAX_ATTEMPTS) continue
        const last = lastAttemptRef.current.get(id) ?? 0
        if (now - last < RETRY_MS) continue

        attemptsRef.current.set(id, attempts + 1)
        lastAttemptRef.current.set(id, now)
        inFlightRef.current.add(id)
        messengerApi
          .getConversation(id)
          .then(({ conversation }) => upsertWireConversation(conversation))
          .catch(() => {
            // Silent: this is a background repair, not a user action. The
            // throttle above already schedules the next attempt.
          })
          .finally(() => inFlightRef.current.delete(id))
      }
    }

    scan()
    // Catches conversations added/updated after this effect ran (new list
    // page, a fresh conversation, a reconnect reseed) without re-subscribing
    // via a selector: `wireConversations` changes on nearly every store
    // write, which would otherwise re-run this on every message.
    const unsubscribe = useMessengerStore.subscribe(scan)
    // Also re-check on a timer so a throttled conversation gets retried even
    // if nothing else in the store changes for a while.
    const interval = setInterval(scan, RETRY_MS)

    // A row that burned through MAX_ATTEMPTS while the client was offline
    // (or the tab was backgrounded and the socket suspended) never gets
    // another try for the rest of the session otherwise, leaving its badge
    // stuck at 0 even once the connection is fine again. Coming back online,
    // or the tab becoming visible again, is exactly the moment worth giving
    // it a fresh three strikes.
    const retryAfterReconnect = (): void => {
      lastAttemptRef.current.clear()
      attemptsRef.current.clear()
      scan()
    }
    const onVisibilityChange = (): void => {
      if (!document.hidden) retryAfterReconnect()
    }
    window.addEventListener('online', retryAfterReconnect)
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      unsubscribe()
      clearInterval(interval)
      window.removeEventListener('online', retryAfterReconnect)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [presenceId, upsertWireConversation])
}
